/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Form, Image, Alert } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faExclamationCircle,
  faCheckCircle,
  faCheck,
  faCircleDot,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { IMAGES } from "../../../constants/imagePath";
import Loader from "../../../component/loader";
import { publicGetRequest, publicPostRequest } from "../../../axios/interceptors";
import { API_ENDPOINTS } from "../../../constants/api";

interface UserInviteDetails {
  firstName: string;
  lastName: string;
  email: string;
  organizationName?: string;
}

const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserInviteDetails | null>(null);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setErrorMessage("No invitation token found in link. Please check your invitation email.");
        setPageLoading(false);
        return;
      }

      try {
        const response = await publicGetRequest(
          `${API_ENDPOINTS.VERIFY_INVITE_TOKEN}?token=${encodeURIComponent(token)}`
        );
        if (response?.data?.success) {
          setUserDetails(response.data.data);
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setErrorMessage(response?.data?.message || "Invitation link is invalid or expired.");
        }
      } catch (err: any) {
        setTokenValid(false);
        setErrorMessage(
          err?.response?.data?.message ||
            "This invitation link has expired or is invalid. Please contact your administrator to resend the invite."
        );
      } finally {
        setPageLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character."
      ),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const response = await publicPostRequest(API_ENDPOINTS.SETUP_PASSWORD, {
          token,
          password: values.password,
        });

        if (response?.data?.success) {
          toast.success(response.data.message || "Password set up successfully! Please log in.");
          navigate("/login");
        } else {
          toast.error(response?.data?.message || "Failed to set up password.");
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "An error occurred while setting up your password.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const password = formik.values.password || "";
  const reqMinLength = password.length >= 8;
  const reqUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const reqNumber = /\d/.test(password);
  const reqSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const metCount = [reqMinLength, reqUpperLower, reqNumber, reqSpecial].filter(Boolean).length;
  const allMet = metCount === 4;

  const passwordRequirements = [
    { label: "8+ characters", met: reqMinLength },
    { label: "Upper & lowercase", met: reqUpperLower },
    { label: "At least 1 number", met: reqNumber },
    { label: "At least 1 symbol (!@#$%^&*)", met: reqSpecial },
  ];

  return (
    <div>
      <div className="login card-bg">
        <div className="card">
          <Container>
            <Row>
              <Col xl={6} lg={12} md={12} sm={12} className="bg-blue d-flex align-items-center">
                <div className="lt-text text-center">
                  <Image src={IMAGES.darkLogo} alt="PatientCampaign Logo" />
                  <h1 className="title">
                    Improve patient engagement with personalized campaigns
                  </h1>
                </div>
              </Col>
              <Col xl={6} lg={12} md={12} sm={12} className="bg-white">
                <div className="rt-content">
                  <Image src={IMAGES.blueLogo} alt="PatientCampaign" />
                  
                  {pageLoading ? (
                    <div className="text-center py-5">
                      <Loader />
                      <p className="mt-3 text-muted">Verifying invitation link...</p>
                    </div>
                  ) : !tokenValid ? (
                    <div className="text-center py-4">
                      <div className="mb-3 text-danger" style={{ fontSize: "48px" }}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      </div>
                      <h3 className="mb-3" style={{ color: "#13294B" }}>Invitation Link Expired or Invalid</h3>
                      <Alert variant="danger" className="text-start mb-4">
                        {errorMessage}
                      </Alert>
                      <Button
                        variant="primary"
                        className="btn-theme w-100"
                        onClick={() => navigate("/login")}
                      >
                        Back to Login
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="heading-title mb-4">
                        <h2>Set Password</h2>
                        <p className="text-muted small mt-1 mb-0">
                          Create a secure password for <strong>{userDetails?.email}</strong>
                        </p>
                      </div>

                      <Form className="input-form" onSubmit={formik.handleSubmit} noValidate>
                        <Form.Group controlId="formPassword" className="position-relative form-group">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.password && !!formik.errors.password}
                            required
                          />
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            className="eye-icon"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.password}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formConfirmPassword" className="position-relative form-group">
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                            required
                          />
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                            className="eye-icon"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <div className="mb-3 px-1">
                          <Row className="g-1">
                            {passwordRequirements.map((req, idx) => (
                              <Col xs={6} key={idx}>
                                <div
                                  className={`d-flex align-items-center ${
                                    req.met ? "text-success fw-medium" : "text-muted"
                                  }`}
                                  style={{ fontSize: "11.5px", transition: "all 0.15s ease" }}
                                >
                                  <FontAwesomeIcon
                                    icon={req.met ? faCheck : faCircleDot}
                                    className={`flex-shrink-0 ${req.met ? "text-success" : "text-secondary opacity-40"}`}
                                    style={{ fontSize: "10px", marginRight: "6px" }}
                                  />
                                  <span>{req.label}</span>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>

                        <Button
                          variant="primary"
                          type="submit"
                          className="btn-theme w-100"
                          disabled={submitting}
                        >
                          {submitting && <Loader />}
                          Set Password
                        </Button>
                      </Form>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
